/**
 * InterviewFlow.studio — City Hall–focused navbar (portals, advisor, Studio).
 */
import React, { useState } from 'react';
import {
  Box,
  Button,
  Container,
  Link,
  Menu,
  MenuItem,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import BrandLogo from '../BrandLogo';
import LanguageSelector from '../LanguageSelector';

const NAV_LINK = {
  color: 'rgba(30, 30, 30, 0.85)',
  fontWeight: 600,
  letterSpacing: '0.02em',
  textTransform: 'none',
  fontSize: '0.9rem',
  transition: 'color 0.2s ease',
  '&:hover': { color: '#1565c0' },
};

const PORTALS = [
  { label: 'Advisor', path: '/v2/demo/permit', testId: 'advisor' },
  { label: 'Business', path: '/v2/demo/permit/business', testId: 'business' },
  { label: 'Residential', path: '/v2/demo/permit/residential', testId: 'residential' },
  { label: 'Events', path: '/v2/demo/permit/events', testId: 'events' },
  { label: 'Zoning check', path: '/zoning-check', testId: 'zoning' },
];

const PS_BASE = 'https://interviewflow.studio/publicSector';
const CITY_SYSTEM = [
  { label: 'Citizen portal', href: `${PS_BASE}/lnp/public`, testId: 'citizens', external: true },
  { label: 'Agency portal', href: `${PS_BASE}/`, testId: 'agencies', external: true },
  { label: 'Staff sign-in', href: `${PS_BASE}/sign-in`, testId: 'staff', external: true },
];

export default function InterviewFlowNavbar() {
  const navigate = useNavigate();
  const theme = useTheme();
  const isSFF = useMediaQuery(theme.breakpoints.down('sm'));
  const [hovered, setHovered] = useState(null);
  const [portalsEl, setPortalsEl] = useState(null);
  const [cityEl, setCityEl] = useState(null);

  const scrollOrGo = (id, fallbackPath) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
    else navigate(fallbackPath);
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        pt: 2,
        px: { xs: 1, sm: 2 },
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: { xs: 1, sm: 2 },
            px: { xs: 1.5, sm: 3 },
            py: 1.5,
            minWidth: 0,
            borderRadius: '999px',
            background: 'rgba(255, 255, 255, 0.88)',
            backdropFilter: 'blur(16px)',
            WebkitBackdropFilter: 'blur(16px)',
            border: '1px solid rgba(255, 255, 255, 0.6)',
            boxShadow: '0 2px 12px rgba(0, 0, 0, 0.04), 0 0 0 1px rgba(25, 118, 210, 0.06)',
          }}
        >
          <Box
            component="button"
            type="button"
            onClick={() => navigate('/')}
            aria-label="InterviewFlow home"
            sx={{
              border: 'none',
              background: 'none',
              cursor: 'pointer',
              p: 0,
              display: 'flex',
              alignItems: 'center',
              flexShrink: 0,
            }}
          >
            <BrandLogo size="small" orientation="horizontal" showTagline={false} iconSize={isSFF ? 28 : 36} />
          </Box>

          <Stack
            direction="row"
            spacing={2}
            useFlexGap
            flexWrap="wrap"
            justifyContent="center"
            sx={{ display: { xs: 'none', sm: 'flex' } }}
          >
            <Link
              component="button"
              type="button"
              data-testid="if-nav-portals"
              onClick={(e) => setPortalsEl(e.currentTarget)}
              sx={{ ...NAV_LINK, color: hovered === 'portals' || portalsEl ? '#1565c0' : 'inherit' }}
              onMouseEnter={() => setHovered('portals')}
              onMouseLeave={() => setHovered(null)}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>Portals</Typography>
            </Link>
            <Menu
              anchorEl={portalsEl}
              open={Boolean(portalsEl)}
              onClose={() => setPortalsEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              {PORTALS.map((s) => (
                <MenuItem
                  key={`${s.label}-${s.path}`}
                  data-testid={`if-nav-portal-${s.testId}`}
                  onClick={() => {
                    setPortalsEl(null);
                    navigate(s.path);
                  }}
                >
                  {s.label}
                </MenuItem>
              ))}
              <MenuItem
                onClick={() => {
                  setPortalsEl(null);
                  scrollOrGo('portals', '/#portals');
                }}
              >
                All portals
              </MenuItem>
            </Menu>
            <Link
              component="button"
              type="button"
              data-testid="if-nav-advisor"
              onClick={() => navigate('/v2/demo/permit')}
              sx={{ ...NAV_LINK, color: hovered === 'advisor' ? '#1565c0' : 'inherit' }}
              onMouseEnter={() => setHovered('advisor')}
              onMouseLeave={() => setHovered(null)}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>Advisor</Typography>
            </Link>
            <Link
              component="button"
              type="button"
              data-testid="if-nav-city-system"
              onClick={(e) => setCityEl(e.currentTarget)}
              sx={{ ...NAV_LINK, color: hovered === 'city' || cityEl ? '#1565c0' : 'inherit' }}
              onMouseEnter={() => setHovered('city')}
              onMouseLeave={() => setHovered(null)}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>City system</Typography>
            </Link>
            <Menu
              anchorEl={cityEl}
              open={Boolean(cityEl)}
              onClose={() => setCityEl(null)}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            >
              {CITY_SYSTEM.map((s) => (
                <MenuItem
                  key={s.testId}
                  data-testid={`if-nav-city-${s.testId}`}
                  component="a"
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setCityEl(null)}
                >
                  {s.label}
                </MenuItem>
              ))}
              <MenuItem
                onClick={() => {
                  setCityEl(null);
                  scrollOrGo('city-system', '/#city-system');
                }}
              >
                Overview on this page
              </MenuItem>
            </Menu>
            <Link
              component="button"
              type="button"
              data-testid="if-nav-build"
              onClick={() => navigate('/studio')}
              sx={{ ...NAV_LINK, color: hovered === 'build' ? '#1565c0' : 'inherit' }}
              onMouseEnter={() => setHovered('build')}
              onMouseLeave={() => setHovered(null)}
            >
              <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '0.9rem' }}>Build</Typography>
            </Link>
          </Stack>

          <Stack direction="row" spacing={isSFF ? 1 : 1.5} alignItems="center" flexWrap="nowrap" sx={{ minWidth: 0 }}>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
              <LanguageSelector />
            </Box>
            <Button
              onClick={() => navigate('/signin')}
              variant="text"
              size={isSFF ? 'small' : 'medium'}
              sx={{
                color: 'rgba(30, 30, 30, 0.75)',
                fontWeight: 600,
                textTransform: 'none',
                fontSize: isSFF ? '0.8rem' : '0.9rem',
                minWidth: isSFF ? 'auto' : undefined,
              }}
            >
              Login
            </Button>
            <Button
              onClick={() => navigate('/v2/demo/permit')}
              variant="contained"
              size={isSFF ? 'small' : 'medium'}
              sx={{
                background: 'linear-gradient(90deg, #1976d2 0%, #42a5f5 70%, #ffb74d 140%)',
                color: 'white',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: '999px',
                px: isSFF ? 1.5 : 2.5,
                boxShadow: 'none',
                '&:hover': { boxShadow: '0 4px 14px rgba(25, 118, 210, 0.35)' },
              }}
            >
              {isSFF ? 'Try' : 'Try advisor'}
            </Button>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}
